
import React, { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { inventoryData } from '../data/inventory';
import { PageHeader, Card, Table, Thead, Tbody, Tr, Th, Td } from '../components/UI';
import { Package, AlertCircle, Check, AlertTriangle, RefreshCw } from '../components/Icons';
import { StockStatus } from '../types';
import { useTranslation } from '../contexts/LanguageContext';

const SupplierDashboard: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();

  // Filter inventory items to show only those provided by this supplier
  const myProducts = useMemo(() => {
    if (!user || !user.companyName) return [];
    return inventoryData.filter(item => item.supplier === user.companyName);
  }, [user]);

  const stats = {
    total: myProducts.length,
    critical: myProducts.filter(i => i.status === StockStatus.CRITICAL).length,
    warning: myProducts.filter(i => i.status === StockStatus.WARNING).length,
    healthy: myProducts.filter(i => i.status === StockStatus.HEALTHY).length,
  };

  const getStatusBadge = (status: StockStatus) => {
    switch (status) {
      case StockStatus.HEALTHY:
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-black uppercase tracking-widest bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-900/30">
            <Check size={10} strokeWidth={4} /> {t('healthy')}
          </span>
        );
      case StockStatus.CRITICAL:
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-black uppercase tracking-widest bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-900/30">
            <AlertCircle size={10} strokeWidth={4} /> {t('critical')}
          </span>
        );
      case StockStatus.WARNING:
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-black uppercase tracking-widest bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-900/30">
            <AlertTriangle size={10} strokeWidth={4} /> {t('warning')}
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="p-6 space-y-8 max-w-[1920px] mx-auto animate-in fade-in duration-500">
      <PageHeader 
        title={t('supplier_portal')}
        breadcrumbs={[{ label: user?.companyName || 'Supplier' }, { label: t('stock_overview') }]}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-6 border-l-4 border-l-brand-medium">
           <div className="flex justify-between items-start">
              <div>
                 <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1">{t('my_products')}</p>
                 <h3 className="text-3xl font-black text-brand-dark dark:text-white">{stats.total}</h3>
              </div>
              <div className="p-2 bg-gray-100 dark:bg-[#262626] rounded-full text-brand-medium">
                 <Package size={24} />
              </div>
           </div>
        </Card>

        <Card className="p-6 border-l-4 border-l-red-500">
           <div className="flex justify-between items-start">
              <div>
                 <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1">{t('critical_stock_levels')}</p>
                 <h3 className="text-3xl font-black text-red-600 dark:text-red-400">{stats.critical}</h3>
              </div>
              <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-full text-red-600">
                 <AlertCircle size={24} />
              </div>
           </div>
        </Card>

        <Card className="p-6 border-l-4 border-l-amber-500">
           <div className="flex justify-between items-start">
              <div>
                 <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1">{t('warnings')}</p>
                 <h3 className="text-3xl font-black text-amber-600 dark:text-amber-400">{stats.warning}</h3>
              </div>
              <div className="p-2 bg-amber-50 dark:bg-amber-900/20 rounded-full text-amber-600">
                 <AlertTriangle size={24} />
              </div>
           </div>
        </Card>
      </div>

      <Card className="overflow-hidden border-t-4 border-t-brand-medium">
         <Table>
            <Thead>
               <Tr isHeader>
                  <Th>{t('item_number')}</Th>
                  <Th>{t('item_name')}</Th>
                  <Th className="text-right">{t('current_stock')}</Th>
                  <Th className="text-right">{t('min_stock')}</Th>
                  <Th>{t('status')}</Th>
                  <Th className="text-right">{t('last_updated')}</Th>
               </Tr>
            </Thead>
            <Tbody>
               {myProducts.length > 0 ? myProducts.map(item => (
                  <Tr key={item.id}>
                     <Td><span className="font-mono font-bold text-brand-dark dark:text-white">{item.itemNumber}</span></Td>
                     <Td className="text-[#161616] dark:text-white">{item.itemName}</Td>
                     <Td className="text-right font-bold">{item.currentStock}</Td>
                     <Td className="text-right text-gray-500">{item.minStock}</Td>
                     <Td>{getStatusBadge(item.status)}</Td>
                     <Td className="text-right text-gray-500 text-xs">
                        <div className="flex items-center justify-end gap-2">
                           {new Date(item.lastUpdated).toLocaleDateString()}
                           <RefreshCw size={12} />
                        </div>
                     </Td>
                  </Tr>
               )) : (
                  <Tr>
                     <Td colSpan={6} className="text-center py-12 text-gray-400 uppercase tracking-widest font-bold">
                        No products found for your company.
                     </Td>
                  </Tr>
               )}
            </Tbody>
         </Table>
      </Card>
    </div>
  );
};

export default SupplierDashboard;
